import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { effect, inject, Injectable, Injector, PLATFORM_ID, signal } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { isIframe } from '../../lib/is-iframe';
import { sleep } from '../../lib/sleep';
import { BrowserService } from '../../services/browser.service';
import { DataLoaderService } from '../../services/data-loader.service';
import { StateService } from '../../services/state.service';
import { StorageService } from '../../services/storage.service';
import { ToastService } from '../../services/toast.service';

import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import type { ClerkOptions, ExternalAccountResource, LocalizationResource, UserResource } from '@clerk/shared/types';
import type { Appearance } from '@clerk/ui';
import { filter, firstValueFrom, from, map, race, take, timer } from 'rxjs';
import { clerkAppearance, defaultLanguage, Language } from '../../consts';
import { GROWTHBOOK } from '../../growthbook';
import { createStaticTranslateKey } from '../../lib/create-static-translate-key';
import { getAllRouteData } from '../../lib/get-all-route-data';
import { isInAppBrowser } from '../../lib/is-in-app-browser';
import { pageLoadLog } from '../../lib/page-load-debug';
import { IGNORE_AUTH_ERRORS, NetworkInterceptorService } from '../../network-interceptor';
import { PICFLOW_ANALYTICS } from '../../picflow-analytics';
import { PICFLOW_CLERK } from '../../picflow-clerk';
import { PICFLOW_CONFIG } from '../../picflow-config';
import { LanguageService } from '../../services/language.service';
import { LazyLoadedSentryService } from '../../services/lazy-loaded-sentry-service';
import { NetworkService } from '../../services/network.service';
import { ThemeService } from '../../services/theme.service';
import {
  clerkCookiesShowSignedOut,
  clerkFailureSentryLevel,
  getClerkCookieSuffix,
  getClerkErrorCode,
  getClerkErrorMessage,
  isAuthPath,
  isClerkLoadTimeout,
  isClerkUiLoadError,
  isDynamicImportClerkError,
  isLocalStorageClerkError,
  isPublicGalleryPage,
  LATE_CLERK_SESSION_RELOAD_KEY,
  MISSING_CLERK_SESSION_RELOAD_KEY,
  needsClerkUiPath,
  shouldShowClerkLoadToast,
  toReportableError,
} from '../lib/clerk-load';

declare global {
  interface Window {
    // Set by @clerk/ui when loaded via loadClerkUIScript
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    __internal_ClerkUICtor?: any;
  }
}

// Import per-locale subpaths: the package root bundles all ~50 locales (~5 MB) into one chunk.
const clerkLocalizations: Record<Language, () => Promise<LocalizationResource>> = {
  en: () => import('@clerk/localizations/en-US').then(m => m.enUS),
  de: () => import('@clerk/localizations/de-DE').then(m => m.deDE),
  fr: () => import('@clerk/localizations/fr-FR').then(m => m.frFR),
  es: () => import('@clerk/localizations/es-ES').then(m => m.esES),
  pt: () => import('@clerk/localizations/pt-PT').then(m => m.ptPT),
  it: () => import('@clerk/localizations/it-IT').then(m => m.itIT),
};

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private state = inject(StateService);
  private loader = inject(DataLoaderService);
  private cookieService = inject(CookieService);
  private storage = inject(StorageService);
  private browserService = inject(BrowserService);
  private toasts = inject(ToastService);
  private networkService = inject(NetworkService);
  private lazyLoadedSentryService = inject(LazyLoadedSentryService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private platformId = inject<string>(PLATFORM_ID);
  private networkInterceptor = inject(NetworkInterceptorService);
  private languageService = inject(LanguageService);
  private analytics = inject(PICFLOW_ANALYTICS);
  private config = inject(PICFLOW_CONFIG);
  private clerkLoader = inject(PICFLOW_CLERK);
  private injector = inject(Injector);
  private themeService = inject(ThemeService);
  clerk: Awaited<ReturnType<typeof this.clerkLoader>>;
  /**
   * Clerk mutates `UserResource` in place. Each listener emission stores a shallow copy so Angular sees a new reference.
   * `externalAccounts` is copied too: a pure pipe only re-runs when that array reference changes.
   */
  readonly clerkUser = signal<UserResource | null>(null);
  private unbindClerkUser?: () => void;
  isIframe: boolean;
  private growthBook = inject(GROWTHBOOK);

  private errorWatcher = this.networkInterceptor.error$.pipe(filter(Boolean)).subscribe(async ({ error, req }) => {
    if (isPlatformServer(this.platformId)) return;
    const code = error?.error?.code;
    if (!['any_authentication_required', 'session_expired', 'forbidden_request'].includes(code)) return;
    if (req.context.get(IGNORE_AUTH_ERRORS)) return;
    const wasAuthenticatedAsUser = !!this.state.tokenData;
    const wasAuthenticatedAsGuest = !!this.state.guestAuthToken;
    this.storage.remove('guest-auth');
    this.storage.removeCookie(
      'guest-auth',
      this.browserService.getTenantCustomDomain(),
      this.browserService.getCorrectPicflowUrl()
    );
    this.state.guestAuthToken = undefined;
    this.state.guest = undefined;
    this.lazyLoadedSentryService.load().then(sentryService =>
      sentryService.captureException('auth_error', {
        code,
        isRetry: !!this.storage.get('reloadedDueToAuthError'),
        wasAuthenticatedAsUser,
        wasAuthenticatedAsGuest,
      })
    );
    await sleep(500);
    // eslint-disable-next-line no-debugger
    debugger;
    if (this.storage.get('reloadedDueToAuthError')) return this.logout();
    this.storage.set('reloadedDueToAuthError', true, true);
    this.browserService.reload(true);
  });

  private pageLoadWatcher = this.router.events
    .pipe(
      filter(event => event instanceof NavigationEnd),
      take(1)
    )
    .subscribe(() => {
      if (isPlatformServer(this.platformId)) return;
      if (!this.storage.get('reloadedDueToAuthError') && !this.storage.get(MISSING_CLERK_SESSION_RELOAD_KEY)) return;
      setTimeout(() => {
        this.storage.remove('reloadedDueToAuthError');
        this.storage.remove(MISSING_CLERK_SESSION_RELOAD_KEY);
      }, 1000 * 60);
    });

  constructor() {
    if (!isPlatformBrowser(this.platformId)) return; // Don't do this on the server
    this.isIframe = isIframe();
    this.tokenInit();

    // Keep mounted Clerk components in sync when the app theme flips.
    effect(() => {
      const theme = this.clerkTheme();
      const clerk = this.clerk as { __unstable__updateProps?: (props: { appearance: Appearance }) => void } | undefined;
      clerk?.__unstable__updateProps?.({ appearance: clerkAppearance(theme) });
    });
  }

  reportedClerkError = false;
  reportedClerkUiError = false;
  private clerkLoadedWithUi = false;

  /** Resolved theme for Clerk appearance. */
  private clerkTheme(): 'light' | 'dark' {
    return this.themeService.resolvedTheme();
  }

  private getClerkAppearance(): Appearance {
    return clerkAppearance(this.clerkTheme());
  }

  private async loadLocale(locale: Language | undefined) {
    const localization = locale ? await clerkLocalizations[locale]?.() : undefined;

    const overrides = {
      formFieldInputPlaceholder__emailAddress: 'Email',
      formFieldInputPlaceholder__password: 'Password',
      formFieldLabel__emailAddress: 'Email',
      formFieldLabel__password: 'Password',
      formFieldInputPlaceholder__backupCode: 'Backup Code',
      socialButtonsBlockButton: '{{provider|titleize}}',
      socialButtonsBlockButtonManyInView: '{{provider|titleize}}',
    } as Partial<LocalizationResource>;

    if (locale === defaultLanguage) {
      // Merge english base localization with overrides
      return {
        ...localization,
        ...overrides,
      };
    }

    // Merge overrides with imported localization for other languages
    return localization
      ? {
          ...localization,
          ...overrides,
        }
      : overrides;
  }

  private getClerkConfig(localization: LocalizationResource) {
    const settings: ClerkOptions = {
      signInUrl: '/auth/login',
      signUpUrl: '/auth/signup',
      touchSession: false,
      standardBrowser: true,
      polling: false,
      localization,
      telemetry: false,
      appearance: this.getClerkAppearance(),
      routerPush: url => {
        if (!url.includes('__nothing__')) {
          this.router.navigateByUrl(url);
        }
      },
      routerReplace: url => {
        if (!url.includes('__nothing__')) {
          this.router.navigateByUrl(url, { replaceUrl: true });
        }
      },
    };

    return settings;
  }

  private shouldLoadUiOnInit(): boolean {
    return needsClerkUiPath(location.pathname);
  }

  private isPublicGalleryPage(): boolean {
    return isPublicGalleryPage(location.pathname, location.hostname, this.browserService.isTenantDomain());
  }

  private clerkFailureContext(err?: unknown) {
    return {
      code: getClerkErrorCode(err),
      message: getClerkErrorMessage(err),
      url: location.href,
      inAppBrowser: isInAppBrowser(),
      isPublicGallery: this.isPublicGalleryPage(),
      isAuthRoute: isAuthPath(location.pathname),
    };
  }

  private async loadClerkUi() {
    if (this.browserService.isExtension()) {
      console.log('skipping clerk ui load in extension');
      return;
    }
    const { loadClerkUIScript } = await import('@clerk/shared/loadClerkJsScript');
    await loadClerkUIScript({ publishableKey: this.config.clerkKey });
    if (!window.__internal_ClerkUICtor) {
      throw new Error('Failed to load Clerk UI');
    }
    return window.__internal_ClerkUICtor;
  }

  private async tryLoadClerkUi() {
    try {
      return await this.loadClerkUi();
    } catch (err) {
      this.reportClerkFailure(err);
      return;
    }
  }

  private async loadClerkInstance(
    clerk: Awaited<ReturnType<typeof this.clerkLoader>>,
    ClerkUI?: Awaited<ReturnType<AuthService['loadClerkUi']>>
  ) {
    const localization = await this.loadLocale(this.languageService.currentLanguage());
    const settings = this.getClerkConfig(localization);
    const clerkLoadStart = Date.now();
    const clerkLoaded = await firstValueFrom(
      race([
        timer(60_000).pipe(map(() => false)),
        from(clerk.load({ ...settings, ...(ClerkUI ? { ui: { ClerkUI } } : {}) })).pipe(map(() => true)),
      ])
    );
    if (!clerkLoaded) {
      throw { message: 'CLERK_LOAD_TIMEOUT' };
    }
    this.analytics.clerkLoadTime = Date.now() - clerkLoadStart;
    this.state.impersonating = !!clerk.session?.actor;
    if (ClerkUI) this.clerkLoadedWithUi = true;
    this.bindClerkUser(clerk);
  }

  private bindClerkUser(clerk: NonNullable<AuthService['clerk']>) {
    this.unbindClerkUser?.();
    this.unbindClerkUser = clerk.addListener(({ user }) => {
      this.clerkUser.set(this.snapshotClerkUser(user));
    });
  }

  private snapshotClerkUser(user: UserResource | null | undefined): UserResource | null {
    if (!user) return null;
    return { ...user, externalAccounts: [...user.externalAccounts] };
  }

  private async ensureClerkUi() {
    if (this.browserService.isExtension()) return;
    if (this.clerkLoadedWithUi) return;
    return this.loader.get('clerkUi', async () => {
      const ClerkUI = await this.loadClerkUi();
      if (!this.clerk || !this.clerk.loaded) {
        if (!this.clerk) this.clerk = await this.clerkLoader();
        await this.loadClerkInstance(this.clerk, ClerkUI);
        return true;
      }
      if (this.clerkLoadedWithUi) return true;
      const clerk = await this.clerkLoader();
      await this.loadClerkInstance(clerk, ClerkUI);
      this.clerk = clerk;
      return true;
    });
  }

  private reportClerkFailure(err: unknown) {
    const isUiError = isClerkUiLoadError(err);
    if (isUiError ? this.reportedClerkUiError : this.reportedClerkError) return;
    if (isUiError) this.reportedClerkUiError = true;
    else this.reportedClerkError = true;

    console.error('clerk failed to load', err);
    if (isClerkLoadTimeout(err)) return;

    const context = this.clerkFailureContext(err);
    const showToast = shouldShowClerkLoadToast(location.pathname);

    if (isLocalStorageClerkError(err)) {
      this.lazyLoadedSentryService
        .load()
        .then(sentryService => sentryService.captureException(toReportableError(err, 'clerk failed to load'), context));
      if (showToast) {
        this.toasts.add(
          createStaticTranslateKey(
            'Failed to load authentication service due to browser security settings',
            'auth-service.failed-browser-security-toast'
          ),
          'error',
          {
            duration: 10_000,
          }
        );
      }
      return;
    }

    if (isDynamicImportClerkError(err)) {
      if (showToast) {
        this.toasts.add(
          createStaticTranslateKey(
            'Failed to load authentication service due to browser security settings',
            'auth-service.failed-browser-security-toast'
          ),
          'error',
          {
            duration: 10_000,
          }
        );
      }
      return;
    }

    const level = clerkFailureSentryLevel({
      isUiError,
      inAppBrowser: context.inAppBrowser,
      isPublicGallery: context.isPublicGallery,
    });
    this.lazyLoadedSentryService
      .load()
      .then(sentryService =>
        sentryService.captureException(toReportableError(err, 'clerk failed to load'), context, level)
      );
    if (showToast) {
      this.toasts.add(
        createStaticTranslateKey('Failed to load authentication service', 'auth-service.failed-to-load-toast'),
        'error',
        { duration: 10_000 }
      );
    }
  }

  private async clerkInit() {
    if (
      location.search.includes('preview=true') ||
      this.state.galleryPreviewMode ||
      this.storage.get('galleryPreviewMode') === true
    ) {
      console.log('skipping clerk init in preview mode');
      return;
    }
    if (this.browserService.getTenantCustomDomain() && !this.growthBook.isOn('custom-domain-auth')) return;
    try {
      pageLoadLog('clerk: import start');
      const clerk = await this.clerkLoader();
      this.clerk = clerk;
      pageLoadLog('clerk: imported');
      const ClerkUI = this.shouldLoadUiOnInit() ? await this.tryLoadClerkUi() : undefined;
      await this.loadClerkInstance(clerk, ClerkUI);
      pageLoadLog('clerk: loaded', { session: !!clerk.session });
    } catch (err) {
      this.reportClerkFailure(err);
    }
  }

  async init() {
    if (isPlatformServer(this.platformId)) return;
    return this.loader.get('authInit', async () => {
      return new Promise<boolean>(resolve => {
        this.clerkInit().then(() => resolve(true));
      });
    });
  }

  getTokenInitError() {
    if (isPlatformServer(this.platformId)) return 'server';
    if (this.isIframe && !this.browserService.isExtension()) return 'iframe';
    if (this.browserService.getTenantCustomDomain() && !this.growthBook.isOn('custom-domain-auth')) {
      return 'tenantDomain';
    }
  }

  async tokenInit() {
    if (this.getTokenInitError()) return;
    await this.init();
    return this.loader.get('authData', this.setupTokenData.bind(this));
  }

  private getLocalStorageReferralCode() {
    const data = this.storage.get<{ code: string; expiresAt: number } | string>('referralCode');
    if (!data) return;
    if (typeof data === 'string') return data;
    if (data.expiresAt && Date.now() > data.expiresAt) return;
    return data.code;
  }

  public async mountSignUp(el: HTMLDivElement) {
    await this.init();
    try {
      await this.ensureClerkUi();
    } catch (err) {
      this.reportClerkFailure(err);
      return;
    }
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    this.clerk?.mountSignUp(el, {
      initialValues: { emailAddress: this.route.snapshot.queryParams.email },
      fallbackRedirectUrl: `${window.location.origin}/auth/authorize`,
      unsafeMetadata: { ref: this.getLocalStorageReferralCode(), timezone },
      routing: 'path',
      path: getAllRouteData(this.route.snapshot).clerkBasePath || '/auth/signup',
      appearance: this.getClerkAppearance(),
    });
  }

  public async unmountSignUp(el: HTMLDivElement) {
    this.clerk?.unmountSignUp(el);
  }

  public async mountSignIn(el: HTMLDivElement, returnUrl?: string) {
    await this.init();
    try {
      await this.ensureClerkUi();
    } catch (err) {
      this.reportClerkFailure(err);
      return;
    }
    this.clerk?.mountSignIn(el, {
      fallbackRedirectUrl: `${window.location.origin}/auth/authorize${returnUrl ? `?returnUrl=${encodeURIComponent(returnUrl)}` : ''}`,
      routing: 'path',
      path: getAllRouteData(this.route.snapshot).clerkBasePath || '/auth/login',
      initialValues: { emailAddress: this.route.snapshot.queryParams.email || this.clerk.client?.signIn?.identifier },
      appearance: this.getClerkAppearance(),
    });
  }

  public async unmountSignIn(el: HTMLDivElement) {
    this.clerk?.unmountSignIn(el);
  }

  async logout(redirectToLogin = true) {
    if (isPlatformServer(this.platformId)) return;
    await this.init();

    this.storage.remove('activeTenantId');
    this.storage.remove('backupTenantId');
    this.cookieService.delete('picflow-loggedin', '/');
    this.storage.remove('promo');
    this.storage.remove('referralCode');
    this.state.loggingOut.set(true);
    await this.clerk?.signOut({ redirectUrl: `${window.location.origin}/auth/__nothing__` });
    if (redirectToLogin) {
      window.onbeforeunload = null;
      window.location.href = `${window.location.origin}/auth/login`;
    }
    await sleep(1000);
    this.state.loggingOut.set(false);
  }

  private async setupTokenData() {
    if (this.browserService.getEnv() !== 'production') {
      const tokenFromCookie = this.cookieService.get('test-auth');
      if (tokenFromCookie) {
        this.state.tokenData = { getIdToken: async () => tokenFromCookie };
        this.state.tokenData$.next(this.state.tokenData);
        return this.state.tokenData;
      }
    }

    let session = this.clerk?.session;
    if (!session) {
      console.debug('no clerk session');
      await sleep(0);
      session = this.clerk?.session;
      console.debug('clerk session after sleep', !!this.clerk, !!this.clerk?.session, !!this.clerk?.user);
      if (!session) return false;
    }

    this.state.tokenData = {
      getIdToken: options => this.getAuthToken(undefined, options?.skipCache),
    };
    this.state.tokenData$.next(this.state.tokenData);

    this.lazyLoadedSentryService.load().then(sentryService => sentryService.setUser({ clerk_id: session.user?.id }));
    return await session.getToken({ template: this.getDefaultTemplate() });
  }

  public async getTokenData() {
    await this.tokenInit();
    return this.state.tokenData;
  }

  private signedOutAtLoad?: Promise<boolean>;

  /**
   * Like `getTokenData`, but on tenant subdomains resolves right away when Clerk's cookie shows nobody is
   * signed in, so anonymous gallery visitors don't wait for Clerk. If Clerk finds a session after all, the page reloads once: guards,
   * redirects and cached gallery data then run as that user.
   */
  public async getTokenDataUnlessSignedOut() {
    if (isPlatformServer(this.platformId)) return undefined;
    this.signedOutAtLoad ??= this.checkSignedOutAtLoad();
    if (await this.signedOutAtLoad) return this.state.tokenData;
    return this.getTokenData();
  }

  private async checkSignedOutAtLoad() {
    if (!this.browserService.getTenantSubDomain()) return false;
    if (this.storage.get(LATE_CLERK_SESSION_RELOAD_KEY)) return false;
    if (this.browserService.getEnv() !== 'production' && this.cookieService.get('test-auth')) return false;
    let signedOut: boolean;
    try {
      const cookieSuffix = await getClerkCookieSuffix(this.config.clerkKey);
      signedOut = clerkCookiesShowSignedOut(this.cookieService.getAll(), cookieSuffix);
    } catch {
      return false;
    }
    pageLoadLog('clerk: cookie checked', { signedOut });
    if (signedOut) {
      this.tokenInit().then(() => {
        if (!this.state.tokenData) return;
        pageLoadLog('clerk: session found after rendering signed out, reloading');
        this.storage.set(LATE_CLERK_SESSION_RELOAD_KEY, true, true);
        this.browserService.reload(true);
      });
    }
    return signedOut;
  }

  private getDefaultTemplate() {
    return this.browserService.isExtension() ? undefined : 'picflow_frontend';
  }

  private lastSkipCache = Date.now();
  private handlingMissingSession = false;
  public async getAuthToken(template = this.getDefaultTemplate(), skipCache = false, isRetry = false): Promise<string> {
    await this.init();
    if (skipCache) {
      if (Date.now() - this.lastSkipCache < 1000) {
        console.debug('skipping cache too fast');
        skipCache = false;
      } else {
        this.lastSkipCache = Date.now();
      }
    }
    if (this.clerk?.session) {
      return await this.clerk.session.getToken({ template, skipCache });
    }

    if (!isRetry) {
      await sleep(1000);
      await firstValueFrom(this.networkService.online$);
      return this.getAuthToken(template, skipCache, true);
    }

    if (!this.clerk?.loaded || this.handlingMissingSession) return;

    console.debug('user after retry', this.clerk.user);
    this.handlingMissingSession = true;
    const alreadyReloaded = !!this.storage.get(MISSING_CLERK_SESSION_RELOAD_KEY);
    this.lazyLoadedSentryService.load().then(sentryService =>
      sentryService.captureException(
        'no_clerk_session',
        {
          ...this.clerkFailureContext(),
          hasClerk: true,
          hasUser: !!this.clerk?.user,
          alreadyReloaded,
        },
        'warning'
      )
    );
    await sleep(100);
    // eslint-disable-next-line no-debugger
    debugger;
    if (alreadyReloaded) {
      await this.logout();
      return;
    }
    this.storage.set(MISSING_CLERK_SESSION_RELOAD_KEY, true, true);
    this.browserService.reload(true);
    return;
  }

  public async getLinkedAccounts() {
    return this.loader.get('linkedAccounts', async () => {
      return this.clerk.user.externalAccounts;
    });
  }

  public async connectSocialAccount(provider: 'google' | 'apple') {
    await this.withReverification(async () => {
      const existingAccount = this.clerk.user.externalAccounts.find(a => a.provider === provider);
      if (existingAccount) {
        await existingAccount.destroy();
      }

      const account = await this.clerk.user.createExternalAccount({
        strategy: `oauth_${provider}`,
        redirectUrl: window.location.origin + '/a/settings/profile',
      });
      location.href = account.verification.externalVerificationRedirectURL.href;
    });
  }

  public async disconnectSocialAccount(account: ExternalAccountResource) {
    await this.withReverification(() => account.destroy());
    this.loader.remove('linkedAccounts');
  }

  public async addPasswordToSocialAccount(newPassword: string) {
    await this.withReverification(() => this.clerk.user.updatePassword({ newPassword }));
  }

  /** Loaded lazily so the verification dialog can depend on this service. */
  private async withReverification<T>(action: () => Promise<T>): Promise<T> {
    const { SessionReverificationService } = await import('./session-reverification.service');
    return this.injector.get(SessionReverificationService).run(action);
  }

  public async isSsoUser() {
    await this.init();
    if (!this.clerk) return false; // clerk failed to load
    return this.clerk.user.enterpriseAccounts.length > 0;
  }
}
